<template>
  <v-dialog
    v-model="visible"
    fullscreen
    persistent
  >
    <v-card class="bg-super-light-blue">
      <v-container class="mxw-1200">
        <v-card
          v-if="loaded"
          data-testid="agreement-dialog"
          border
          tile
        >
          <v-card-title class="d-flex align-center text-wrap">
            <span>
              {{ $bct(agreement.name) }}
            </span>

            <v-spacer />

            <v-btn
              @click="close()"
              icon="close"
              variant="text"
            />
          </v-card-title>

          <v-divider />

          <template v-if="loaded">
            <v-card-text>
              <QuestionSet
                v-model="agreement"
                :questions="validQuestions"
                :readonly="readonly"
                :schema="schema.definition"
                expanded
                flat
                hide-actions
                outlined-sections
                paddingless
                very-dense
              />

              <div
                v-for="question in verificationQuestions"
                :key="question.id"
              >
                <FormQuestion
                  :color="null"
                  :elevation="0"
                  :subtitle="question.verification_subtitle"
                  :title="question.verification_title"
                  dense
                  hide-actions
                  outlined
                  tile
                  very-dense
                >
                  <template v-if="!readonly">
                    <AttachmentUploader
                      @uploaded="attachments.push($event)"
                      :ref="['uploader', question.id].join('')"
                      :owner="{
                        business_id: $store.state.profile.business_id,
                        type: 'Agreement',
                        id: agreement.id,
                        tag: question.id,
                        tags: [agreement.id, question.id],
                      }"
                      class="mb-4"
                    />
                  </template>

                  <AttachmentList
                    @change="loadAttachments()"
                    @delete="loadAttachments"
                    :attachments="
                      attachments.filter((attachment) => attachment.tags.includes(question.id))
                    "
                    class="mb-6"
                  />
                </FormQuestion>
              </div>
            </v-card-text>
          </template>

          <template v-else>
            <v-progress-linear indeterminate />
          </template>

          <v-divider />

          <v-card-actions class="d-flex flex-column flex-sm-row ga-3 pa-4">
            <div class="w-100 flex-sm-grow-0">
              <LabeledButton
                @click="close"
                :disabled="false"
                :full-width="$vuetify.display.xs"
                :size="'large'"
                class="w-100 w-sm-auto"
                color="primary"
                text="Close"
                variant="outlined"
              />
            </div>

            <v-spacer class="d-none d-sm-flex" />

            <template v-if="!readonly">
              <LabeledButton
                @click="saveDraft"
                :disabled="false"
                :full-width="$vuetify.display.xs"
                :loading="processing"
                :size="'large'"
                class="w-100 w-sm-auto"
                color="primary"
                text="Save draft"
                variant="outlined"
              />

              <LabeledButton
                @click="submit"
                :disabled="!completed"
                :full-width="$vuetify.display.xs"
                :loading="processing"
                :size="'large'"
                class="w-100 w-sm-auto ml-0 ml-sm-2"
                color="primary"
                data-testid="agreement-submit-button"
                text="Finish and submit"
              />
            </template>
          </v-card-actions>
        </v-card>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script setup>
import _ from 'lodash';
import AttachmentList from '@/shared/components/attachments/AttachmentList.vue';
import AttachmentUploader from '@/shared/components/attachments/AttachmentUploader.vue';
import FormQuestion from '@/shared/components/form/FormQuestion.vue';
import LinkedList from '@/shared/services/linked-list';
import QuestionSet from '@/shared/components/form/QuestionSet.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import LabeledButton from '@/shared/components/form/LabeledButton.vue';

const props = defineProps({
  role: {
    type: String,
    default: 'member',
  },
});

const emit = defineEmits(['change']);

const { api } = useApi();
const eventBus = useEventBus();
const router = useRouter();
const route = useRoute();
const store = useStore();
const $bct = inject('translator');

const agreement = ref(null);
const attachments = ref([]);
const group = ref(null);
const loaded = ref(false);
const processing = ref(false);
const schema = ref(null);
const visible = ref(false);
const questions = ref([]);

const incompleteQuestions = computed(() => {
  return mandatoryQuestions.value.filter((question) =>
    question.synced_attributes.some((sa) => {
      const attrParts = sa.name.split('.');
      return !agreement.value.custom[attrParts[attrParts.length - 1]];
    }),
  );
});

const incompleteVerificationQuestions = computed(() => {
  return mandatoryQuestions.value
    .filter((question) => question.verification)
    .filter(
      (question) =>
        attachments.value.filter((attachment) => attachment.tags.includes(question.id)).length ===
        0,
    );
});

const completed = computed(() => {
  return (
    incompleteQuestions.value.length === 0 && incompleteVerificationQuestions.value.length === 0
  );
});

const mandatoryQuestions = computed(() => {
  return validQuestions.value.filter((question) => question.mandatory);
});

const readonly = computed(() => {
  if (!agreement.value) return true;

  if (['Sent', 'Partial'].includes(agreement.value.status)) {
    let nextSignatoryId;
    const orderedSignatures = _.orderBy(agreement.value.signatures, 'ordinal');

    for (const signature of orderedSignatures) {
      if (signature.signed_at) continue;

      nextSignatoryId = signature.signatory_id;
      break;
    }

    if (nextSignatoryId === store.state.profile.id) return false;
  }

  return true;
});

const verificationQuestions = computed(() => {
  return LinkedList.sort(questions.value)
    .filter((question) => question.verification)
    .filter((question) => question.published && question.valid);
});

const validQuestions = computed(() => {
  return LinkedList.sort(questions.value).filter((question) => question.valid);
});

watch(
  () => route.query.agreementId,
  async (newVal) => {
    if (newVal) {
      await open();
    } else {
      close();
    }
  },
  { immediate: true },
);

function agreementApi() {
  switch (props.role) {
    case 'organization':
      return api.organization.agreement;
    case 'manager':
      return api.manager.agreement;
    case 'parent':
      return api.parent.agreement;
    default:
      return {};
  }
}

function attachmentApi() {
  switch (props.role) {
    case 'organization':
      return api.organization.attachment;
    case 'manager':
      return api.manager.attachment;
    case 'parent':
      return api.member.attachment;
    default:
      return {};
  }
}

function close() {
  if (route.query.agreementId) {
    const query = { ...route.query };
    delete query.agreementId;

    if (!query.tab) {
      query.tab = 'forms-and-agreements';
    }

    router.push({ query });
  }
  visible.value = false;
  resetAgreement();
}

async function open(agreementId) {
  resetAgreement();
  visible.value = true;

  const id = agreementId || route.query.agreementId;
  const response = await agreementApi().get(id);
  agreement.value = response.data;

  schema.value = store.state.schemas[agreement.value.schema_id];
  if (!schema.value && agreement.value.schema_id) await loadSchema(agreement.value.schema_id);

  await loadAttachments();
  await loadGroup();
  await loadQuestions();

  loaded.value = true;
}

function resetAgreement() {
  agreement.value = null;
  loaded.value = false;
  schema.value = null;
  questions.value = [];
}

async function saveDraft() {
  processing.value = true;
  agreement.value.submit_requested = false;

  const response = await agreementApi().update(agreement.value.id, agreement.value);
  agreement.value = response.data;
  processing.value = false;
  eventBus.chime('Saved');
}

async function submit() {
  processing.value = true;
  agreement.value.submit_requested = true;

  await agreementApi().update(agreement.value.id, agreement.value);
  processing.value = false;
  eventBus.chime('Submitted successfully');
  emit('change');
  close();
}

async function validate() {
  const assertions = questions.value.map((question) => {
    if (question.conditions.length > 0) {
      return {
        assertion: {
          conditions: question.conditions,
          value: this[question.synced_model.toLowerCase()],
        },
      };
    }
    return { assertion: { conditions: [], value: {} } };
  });

  const { data } = await api.public_api.assertion.promiseBulkCreate(assertions);
  const { results } = data;

  questions.value.forEach((question, index) => {
    // eslint-disable-next-line no-param-reassign
    question.valid = results[index].result;
  });
}

async function loadAttachments() {
  const response = await attachmentApi().index({
    owner_id: agreement.value.id,
    owner_type: 'Agreement',
  });
  if (response?.status !== 200) return;

  attachments.value = response.data;
}

async function loadGroup() {
  if (!store.state.profile?.is_parent) {
    group.value = null;
    return;
  }

  const resp = await api.group.get();
  group.value = resp.data;
}

async function loadQuestions() {
  if (!schema.value) {
    eventBus.error('Agreement not found');
    return;
  }

  await api.public_api.organization.question.index(
    { owner_id: schema.value.id, owner_type: 'Schema' },
    async (response) => {
      questions.value = response.data;
      await validate();
    },
  );
}

async function loadSchema(id) {
  const response = await api.public_api.organization.schema.get(id);
  schema.value = response?.data;
}

defineExpose({
  open,
});
</script>
